import { useCallback, useEffect, useState } from "react";
import { fetchSquaresPrioritisedMatches } from "../components/Squares/shared/squaresApi";
import { isSquaresLobbyEnabled } from "../components/Squares/shared/squaresConstants";
import {
  mapFilterSquaresPlayableLobbyFixturesForHome,
  isSquaresFixtureLiveForChallengeList,
} from "../components/Squares/lobby/squaresLobbyMatchUtils";

/**
 * True when the Squares lobby lists at least one match that counts as live for friend challenges
 * (same fixture-list signal as {@link isSquaresFixtureLiveForChallengeList} without stream).
 * Polls while `enabled` so the friends CHALLENGE control stays in sync with the challenge modal.
 */
export function useLiveSquaresChallengeFromLobby(opts: { enabled: boolean; pollMs?: number }): boolean {
  const { enabled, pollMs = 20000 } = opts;
  const [hasLiveMatch, setHasLiveMatch] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || !isSquaresLobbyEnabled()) {
      setHasLiveMatch(false);
      return;
    }
    try {
      const data = await fetchSquaresPrioritisedMatches();
      const events = Array.isArray(data?.events) ? data.events : [];
      const fixtures = mapFilterSquaresPlayableLobbyFixturesForHome(events);
      // Without stream we can only know live status — game-open needs 410 markets (modal SSE).
      const anyLive = fixtures.some((f) => isSquaresFixtureLiveForChallengeList(f, undefined));
      setHasLiveMatch(anyLive);
    } catch {
      setHasLiveMatch(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setHasLiveMatch(false);
      return;
    }
    void refresh();
    let intervalId = 0;
    const schedule = () => {
      if (intervalId) window.clearInterval(intervalId);
      const hidden = typeof document !== "undefined" && document.visibilityState !== "visible";
      const effectivePollMs = hidden ? Math.max(pollMs, 60000) : pollMs;
      intervalId = window.setInterval(() => void refresh(), effectivePollMs);
    };
    schedule();
    const onVisibilityChange = () => schedule();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      if (intervalId) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, refresh, pollMs]);

  return hasLiveMatch;
}
